import { useSiteDetection } from 'shared/hooks';
import { InfiniteQueryObserverSuccessResult } from 'react-query';
import { useCallback, useMemo } from 'react';
import { ContextMenuItem, Table, useOnScrollDown } from 'redesign';
import { RenderUnsettledUI } from 'shared/components';
import { PaymentUsersInfiniteResponse } from 'api/services/finance/payments.schema';
import { Box } from '@mui/material';
import { getPaymentUsersColumnDefinition } from './payment-users-column-definition';
import {
  PaymentUsersColumnDefinition,
  PaymentUsersParsedData,
  PaymentUsersTableData,
  PaymentUsersTableOptions,
} from './payment-users.types';
import { useGetPaymentUsers } from './hooks/use-get-payment-users';
import { useGetPaymentUsersActionHandlers } from './hooks/use-get-payment-users-actions';
import { usePaymentUsersPage } from './hooks/use-payment-users-page';

type TableProps = {
  infiniteQueryResult: InfiniteQueryObserverSuccessResult<PaymentUsersInfiniteResponse, unknown>;
};

export const PaymentUsersTableDataProvider = () => {
  const { site } = useSiteDetection();
  const { filters: appliedFilters } = usePaymentUsersPage();
  const infiniteQueryResult = useGetPaymentUsers(site, appliedFilters);

  if (infiniteQueryResult.status !== 'success') {
    return <RenderUnsettledUI data={infiniteQueryResult} />;
  }
  return <PaymentUsersTable infiniteQueryResult={infiniteQueryResult} />;
};

const PaymentUsersTable = (props: TableProps) => {
  const { infiniteQueryResult } = props;
  const { filters: appliedFilters } = usePaymentUsersPage();
  const { handleBlockUser, handleUnblockUser } = useGetPaymentUsersActionHandlers(appliedFilters);
  const flatData = useMemo(
    () => infiniteQueryResult.data.pages.flatMap((page) => page.items),
    [infiniteQueryResult.data]
  );

  const getMenuActionsFromRow = useCallback((flexWorkerId: string, isBlocked?: boolean) => {
    return [
      <ContextMenuItem
        key={`block-${flexWorkerId}`}
        disabled={isBlocked}
        onClick={() => handleBlockUser(flexWorkerId)}
      >
        Blokkeren
      </ContextMenuItem>,
      <ContextMenuItem
        key={`unblock-${flexWorkerId}`}
        disabled={!isBlocked}
        onClick={() => handleUnblockUser(flexWorkerId)}
      >
        Deblokkeren
      </ContextMenuItem>,
    ];
  }, []);

  const tableData = useMemo(() => mapTableData(flatData), [flatData]);
  const columns = useMemo(() => getPaymentUsersColumnDefinition(), []);
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: infiniteQueryResult,
    flatData: flatData,
  });

  return (
    <Box minHeight={0}>
      <Table<PaymentUsersColumnDefinition, PaymentUsersTableOptions>
        stickyHeader={true}
        columns={columns}
        data={tableData}
        getRowActions={getMenuActionsFromRow}
        tableStyle={{ tableLayout: 'fixed' }}
        onScroll={fetchIfBottomScrolled}
      ></Table>
    </Box>
  );
};

const mapTableData = (data: PaymentUsersParsedData[]): PaymentUsersTableData[] => {
  return data.map((item) => ({
    ...item,
    workerStatus: {
      status: item.status,
      hasPendingBankAccount: item.hasPendingBankAccount,
      isBlocked: item.isBlocked,
    },
    actions: {
      flexWorkerId: item.flexWorkerId,
      isBlocked: item.isBlocked,
    },
  }));
};
